import React, { useState } from "react";
import { Topbar, SidebarNav } from "components/layout";
import { ProgressBar } from 'components/ui';
import { useAuth } from "contexts/AuthContext";
import { IconBuildingBank } from '@tabler/icons-react';
import { useNavigate } from "react-router-dom";


const WithdrawalPage: React.FC = () => {
    const navigate = useNavigate();
    const [step, setStep] = useState(1);
    const [form, setForm] = useState({
        amount: "",
        bank: "",
        accountNumber: "",
    });
    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const [withdrawalCompleted, setWithdrawalCompleted] = useState(false);

    const { user } = useAuth();
    const balance = parseFloat(user?.wallet_balance || "0.00");


    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
        setErrors((prev) => ({ ...prev, [name]: "" }));
    };


    const validateStep = () => {
        let newErrors: { [key: string]: string } = {};
        if (step === 1) {
            if (!form.amount) {
                newErrors.amount = "Enter an amount";
            } else if (parseFloat(form.amount) > balance) {
                newErrors.amount = "Enter a valid amount within your balance";
            }
        }
        if (step === 2 && !form.bank) newErrors.bank = "Select a bank";
        if (step === 3 && !form.accountNumber) newErrors.accountNumber = "Enter account number";
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleNext = () => {
        if (validateStep()) setStep(step + 1);
    };

    const handleBack = () => setStep(step - 1);

    const handleCancel = () => {
        navigate('/');
    };

    const handleSubmit = () => {
        if (validateStep()) {
            // TODO: call API here
            setWithdrawalCompleted(true);
        }
    };

    // Progress bar width
    const progress = step === 1 ? '25%' : step === 2 ? '50%' : step === 3 ? '75%' : '100%';

    // Confirmation page
    if (withdrawalCompleted) {
        return (
            <div className="min-h-screen bg-gray-50">
                <Topbar />
                <div className="flex">
                    <SidebarNav />
                    <main className="flex-1 flex items-center justify-center p-4">
                        <div className="bg-white rounded-2xl shadow-md w-full max-w-md p-10 flex flex-col items-center">
                            <span className="text-3xl mb-4">💰</span>
                            <h2 className="text-center text-xl font-semibold mb-2">Withdrawal request submitted!</h2>
                            <p className="text-center text-gray-600 mb-6">
                                Your withdrawal of <span className="font-medium">₦{parseFloat(form.amount).toLocaleString()}</span> is being processed.
                                <br />You'll receive it within 1-3 business days.
                            </p>
                            <button
                                className="bg-primary-700 text-white rounded-lg px-6 py-2 font-medium hover:bg-primary-800 transition"
                                onClick={() => setWithdrawalCompleted(false)}
                            >
                                Make another withdrawal
                            </button>
                        </div>
                    </main>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            <Topbar />
            <div className="flex">
                <SidebarNav />
                <main className="flex-1 flex flex-col">
                    {/* Header */}
                    <div className="flex items-center justify-between px-8 py-6">
                        <div className="flex items-center space-x-2">
                            <button
                                className={`text-sm ${step === 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500'}`}
                                onClick={handleBack}
                                disabled={step === 1}
                            >
                                &larr; Back
                            </button>
                        </div>
                        <button className="text-red-500 text-sm font-medium" onClick={handleCancel}>
                            Cancel <span className="text-xl font-light ml-1">&times;</span>
                        </button>
                    </div>

                    {/* Main Content */}
                    <div className="flex flex-1 items-center justify-center px-4">
                        <div className="bg-white rounded-2xl shadow-sm w-full max-w-xl px-6 py-10 sm:px-10 sm:py-12 lg:px-16 lg:py-16">
                            {step < 4 ? (
                                <>
                                    <h2 className="text-center text-2xl font-semibold mb-1">Withdraw Funds</h2>
                                    <p className="text-center text-gray-500 text-sm mb-6">Enter your withdrawal details</p>
                                </>
                            ) : (
                                <>
                                    <h2 className="text-center text-2xl font-semibold mb-1">Review withdrawal</h2>
                                    <p className="text-center text-gray-500 text-sm mb-6">Confirm all details are correct</p>
                                </>
                            )}

                            <ProgressBar progress={progress} />

                            {/* Step 1: Amount */}
                            {step === 1 && (
                                <form className="flex flex-col space-y-6">
                                    <div>
                                        <label className="block text-sm font-semibold mb-1">How much do you want to withdraw?</label>
                                        <div className="relative flex items-center mt-2">
                                            <span className="absolute left-0 top-1/2 -translate-y-1/2 text-3xl font-medium pl-1">
                                                ₦
                                            </span>
                                            <input
                                                type="number"
                                                name="amount"
                                                placeholder="0.00"
                                                value={form.amount}
                                                onChange={handleChange}
                                                className="w-full pl-8 pr-4 bg-transparent border-0 border-b-2 border-gray-200 focus:border-primary-400 rounded-none py-3 text-3xl font-medium focus:outline-none transition placeholder-gray-300"
                                                style={{ letterSpacing: "1px" }}
                                            />
                                        </div>
                                        <div className="mt-2 text-xs text-gray-400">
                                            Withdrawable balance: ₦{balance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                        </div>
                                        {errors.amount && <span className="block text-xs text-red-500 mt-1">{errors.amount}</span>}
                                    </div>

                                    <div className="flex justify-end pt-4">
                                        <button
                                            type="button"
                                            onClick={handleNext}
                                            className="w-[40%] bg-primary-700 text-white rounded-lg py-2 hover:bg-primary-800 transition"
                                        >
                                            Next
                                        </button>
                                    </div>
                                </form>
                            )}

                            {/* Step 2: Bank Selection */}
                            {step === 2 && (
                                <form className="flex flex-col space-y-6">
                                    <div className="flex flex-col justify-center">
                                        <label className="block text-sm font-semibold mb-1">Bank</label>
                                        <div className="relative flex items-center mt-2">
                                            <span className="absolute left-0 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
                                                <IconBuildingBank />
                                            </span>
                                            <select
                                                name="bank"
                                                value={form.bank}
                                                onChange={handleChange}
                                                className="w-full pl-9 pr-4 bg-transparent border-0 border-b-2 border-gray-200 focus:border-primary-400 rounded-none py-3 text-lg font-medium focus:outline-none transition placeholder-gray-300"
                                            >
                                                <option value="">[---]</option>
                                                <option value="GTB">Guaranty Trust Bank (GTBank)</option>
                                                <option value="UBA">United Bank for Africa (UBA)</option>
                                                <option value="Access">Access Bank</option>
                                                <option value="FirstBank">First Bank of Nigeria</option>
                                                <option value="Zenith">Zenith Bank</option>
                                                <option value="Fidelity">Fidelity Bank</option>
                                                <option value="Union">Union Bank</option>
                                            </select>
                                        </div>
                                        <span className="text-xs text-gray-400 mt-1">Choose your bank</span>
                                    </div>

                                    <div className="flex justify-end">
                                        <button
                                            type="button"
                                            onClick={handleNext}
                                            className="w-[40%] bg-primary-700 text-white rounded-lg py-2 hover:bg-primary-800 transition"
                                        >
                                            Next
                                        </button>
                                    </div>
                                </form>
                            )}

                            {/* Step 3: Account Details */}
                            {step === 3 && (
                                <form className="flex flex-col space-y-6">
                                    <div>
                                        <label className="block text-sm font-semibold mb-1">Account number</label>
                                        <input
                                            type="text"
                                            name="accountNumber"
                                            placeholder="1234567890"
                                            value={form.accountNumber}
                                            onChange={handleChange}
                                            className="w-full mt-2 p-4 border border-gray-300 rounded-xl focus:outline-none focus:border-primary-400 transition text-lg"
                                            maxLength={10}
                                        />
                                        {errors.accountNumber && (
                                            <span className="block text-xs text-red-500 mt-1">{errors.accountNumber}</span>
                                        )}
                                    </div>

                                    <div>
                                        <label className="block text-sm font-semibold mb-1 mt-2">Account name</label>
                                        <input
                                            type="text"
                                            name="accountName"
                                            value="The Name"
                                            readOnly
                                            onChange={handleChange}
                                            className="w-full p-4 bg-transparent border rounded-xl text-md text-gray-500 focus:outline-none focus:border-primary-400 transition placeholder-gray-300 cursor-default"
                                        />
                                    </div>

                                    <div className="flex justify-end">
                                        <button
                                            type="button"
                                            onClick={handleNext}
                                            className="w-[40%] bg-primary-700 text-white rounded-lg py-2 hover:bg-primary-800 transition"
                                        >
                                            Next
                                        </button>
                                    </div>
                                </form>
                            )}

                            {/* Step 4: Review */}
                            {step === 4 && (
                                <div className="flex flex-col items-center">
                                    <div className="w-full flex flex-col space-y-3 mb-6">
                                        <div className="flex justify-between text-sm">
                                            <span className="font-medium text-gray-600">Withdrawal amount</span>
                                            <span className="text-gray-800 font-semibold">₦{parseFloat(form.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                                        </div>
                                        <div className="flex justify-between text-sm">
                                            <span className="font-medium text-gray-600">Bank</span>
                                            <span className="text-gray-800">{form.bank}</span>
                                        </div>
                                        <div className="flex justify-between text-sm">
                                            <span className="font-medium text-gray-600">Account number</span>
                                            <span className="text-gray-800">{form.accountNumber}</span>
                                        </div>
                                        <div className="flex justify-between text-sm">
                                            <span className="font-medium text-gray-600">Account name</span>
                                            <span className="text-gray-800">The Name</span>
                                        </div>
                                        <div className="border-t pt-3 mt-3">
                                            <div className="flex justify-between text-sm">
                                                <span className="font-medium text-gray-600">Processing fee</span>
                                                <span className="text-gray-800">₦25.00</span>
                                            </div>
                                            <div className="flex justify-between text-lg font-semibold mt-2 pt-2 border-t">
                                                <span className="text-gray-800">You'll receive</span>
                                                <span className="text-primary-700">₦{(parseFloat(form.amount) - 25).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="w-full p-3 bg-primary-50 border border-primary-100 rounded-lg mb-6">
                                        <div className="text-xs text-primary-700">
                                            <strong>Processing time:</strong> 1-3 business days
                                        </div>
                                    </div>

                                    <button
                                        type="button"
                                        onClick={handleSubmit}
                                        className="w-[40%] bg-primary-700 text-white rounded-lg py-2 hover:bg-primary-800 transition"
                                    >
                                        Withdraw
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </main>
            </div>
        </div>
    );
};

export default WithdrawalPage;